import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { SessionUser, SpaceDetail } from '@dpw/shared';
import { api } from '../api';
import { useConversation } from '../lib/queries';
import { Sheet } from './Sheet';
import { ActivityForm, type ActivityFormValue } from './ActivityForm';
import { ApproveCard } from './ApproveCard';
import { useToast } from './toast';

/** New activity: "Fill it in yourself" saves directly; "Describe it" asks the assistant for an Approve card. */
export function NewActivity({ space, me, trackId, parent, onClose, side = 'right', onNewTrack }: {
  space: SpaceDetail; me: SessionUser; trackId?: string | undefined; onClose: () => void; side?: 'right' | 'bottom';
  /** A sub-activity of this one. */
  parent?: { id: string; title: string } | undefined;
  /** A space with no tracks yet: every activity sits on a track, so offer to make one (when the person may). */
  onNewTrack?: (() => void) | undefined;
}) {
  const [tab, setTab] = useState<'manual' | 'describe'>('manual');
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [text, setText] = useState('');
  const [convId, setConvId] = useState<string>();
  const [reply, setReply] = useState('');
  const conv = useConversation(convId);

  const save = async (v: ActivityFormValue) => {
    setBusy(true); setError(null);
    try {
      const a = await api.activities.create(v.input);
      for (const f of v.files) await api.activities.upload(a.id, f, f.name);
      await qc.invalidateQueries();
      toast(`Created in ${a.trackName}`);
      onClose();
      nav(`/activities/${a.id}`);
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const describe = async () => {
    if (!text.trim()) return;
    setBusy(true); setError(null);
    try {
      const r = await api.assistant.describe({ text: text.trim(), spaceId: space.id, trackId: trackId ?? null });
      setConvId(r.conversation.id);
      setReply(r.reply.content);
      await qc.invalidateQueries();
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const cards = conv.data?.actions ?? [];

  return (
    <Sheet side={side} label={parent ? 'New sub-activity' : 'New activity'} crumbs={<><span>{space.name}</span><span>/</span>{parent && <><span>{parent.title}</span><span>/</span></>}<b>{parent ? 'New sub-activity' : 'New activity'}</b></>} onClose={onClose}>
      {!space.tracks.length ? (
        <div className="col gap12 notracks" role="status">
          <b>Add a track first</b>
          <span className="sm mut">Every activity sits on a track (a workstream), and {space.name} has none yet.</span>
          {onNewTrack ? <div><button type="button" className="bt pri" onClick={() => { onClose(); onNewTrack(); }}>New track</button></div>
            : <span className="sm mut">Ask the space lead or the director to add one.</span>}
        </div>
      ) : <>
      <div className="seg2" role="tablist">
        <button role="tab" aria-selected={tab === 'manual'} className={tab === 'manual' ? 'on' : ''} onClick={() => setTab('manual')}>Fill it in yourself</button>
        <button role="tab" aria-selected={tab === 'describe'} className={tab === 'describe' ? 'on' : ''} onClick={() => setTab('describe')}>Describe it</button>
      </div>
      {tab === 'manual' ? (
        <ActivityForm space={space} me={me} defaultTrackId={trackId} defaultParentId={parent?.id} submitLabel={parent ? 'Create sub-activity' : 'Create activity'} busy={busy} error={error} onSubmit={save} onCancel={onClose} />
      ) : (
        <div className="col gap12">
          <label className="fld">Tell the assistant what this is and what needs to happen: steps, order, owner, when.
            <textarea className="inp" rows={5} value={text} onChange={e => setText(e.target.value)}
              placeholder="For example: data review with IT on Tuesday at 11, Faisal owns it. First get Hani to approve access, then map the 12 source systems." />
          </label>
          <div className="rowc">
            <button className="bt pri" type="button" disabled={busy || !text.trim()} onClick={describe}>{busy ? <span className="spin" /> : null}Let the assistant read it</button>
            <span className="xs mut">Nothing is created until you approve the card.</span>
          </div>
          {reply && <div className="aibox"><span className="k">The assistant</span><div className="sm" style={{ whiteSpace: 'pre-line' }}>{reply}</div></div>}
          {cards.map(a => <ApproveCard key={a.id} action={a} onApproved={x => { const l = x.result?.link; if (typeof l === 'string') { onClose(); nav(l); } }} />)}
          {error ? <div className="err">{error instanceof Error ? error.message : 'Something went wrong.'}</div> : null}
        </div>
      )}
      </>}
    </Sheet>
  );
}
